import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Archive, ArchiveRestore, ArrowLeft, Building2, Copy, GitMerge, Mail, Pencil, Phone, Plus, Trash2, User } from 'lucide-react';
import { AcaoPrimaria } from '../../componentes/acao-primaria';
import { Botao, Cartao, ProvedorCartoesRecolhidos, Selo, type CorSelo } from '../../componentes/base';
import { Chatter } from '../../componentes/chatter';
import { CartaoVerticais } from '../../componentes/verticais';
import { Carregando, Erro } from '../../componentes/estados';
import { BuscaRemota, Formulario, linhasExtras, Rotulo, useCamposPersonalizados, type DefCampo } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { del, get, mensagemDeErro, post } from '../../nucleo/api';
import { humanizar, rotuloValor } from '../../nucleo/rotulos';
import { textoVinculo, type ItemVinculo } from '../../nucleo/vinculos';
import { useCamposDaTela } from '../../nucleo/campos-tela';
import { useSessao } from '../../nucleo/sessao';
import { modulosWeb } from '../../nucleo/modulos-web';
import { ArquivarParceiro } from './ArquivarParceiro';
import { consultar, enderecoDaConsulta } from './consulta';
import { CartaoFiscal } from './FiscalParceiro';
import { FormParceiro } from './FormParceiro';
import { Estrelas, SeloInadimplente, SelosPapel } from './ListaParceiros';
import { chavePixValida, mascaraChaveAleatoria, mascaraTelefone, TIPOS_PIX, type TipoPix } from './pix';
import { linkTitulosDoParceiro, textoInadimplencia } from '../../nucleo/inadimplencia';
import { nomePais, opcoesUf, rotuloTipoDocumento } from './paises';
import { formatarDocumentoPais } from '@malgoos/shared';
import { documentoFormatado, telefoneFormatado, type ContaBancaria, type Ficha } from './tipos';
import { CartaoVinculos } from './VinculosParceiro';
import { CartaoFornecimento } from './FornecimentoParceiro';
import { contaBancaria, email, endereco, nomePessoa, numeroConta, textoLivre } from '../../nucleo/formato';
import { TextoCortado } from '../../componentes/texto-cortado';
import { useVoltar } from '../../nucleo/voltar';

interface GrupoVinculo {
  modulo: string;
  titulo: string;
  itens: ItemVinculo[];
}

function Linha({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <div className="flex min-w-0 justify-between gap-4 py-1.5 text-sm">
      <dt className="shrink-0 text-tinta-3">{rotulo}</dt>
      <TextoCortado como="dd" className="min-w-0 text-right">{children}</TextoCortado>
    </div>
  );
}

/** Conta bancária numa linha (formatadores do modo discreto) e, abaixo, o que a fase 2 acrescentou: tipo, titular, IBAN e SWIFT. */
function LinhaConta({ c }: { c: ContaBancaria }) {
  const { t } = useTranslation('cadastros');
  const extra = [
    c.conta_tipo ? t(`valores.contaTipo.${c.conta_tipo}`) : '',
    c.chave_pix_tipo ? `${t('campos.chavePixTipo')}: ${t(`valores.pixTipo.${c.chave_pix_tipo}`)}` : '',
    c.titular_documento ? `${t('campos.titular')}: ${documentoFormatado(c.titular_documento)}` : '',
    c.iban ? `IBAN ${numeroConta(c.iban)}` : '',
    c.swift ? `SWIFT ${c.swift}` : '',
  ].filter(Boolean);
  return (
    <span className="flex min-w-0 flex-col">
      <span>{[c.banco_codigo, contaBancaria(c)].filter(Boolean).join(' ')}</span>
      {extra.length > 0 && <span className="text-xs text-tinta-3">{extra.join(' · ')}</span>}
    </span>
  );
}

/** M1.7: ficha do parceiro com dados, endereços, contas, contatos, projetos, vínculos e chatter. */
export function FichaParceiro() {
  const { id = '' } = useParams();
  const { t } = useTranslation('cadastros');
  const { t: tn } = useTranslation();
  const s = useSessao();
  const navegar = useNavigate();
  // Lista de 30/10 B (item 6): sem tela anterior no app (link colado, nova aba), a lista de Pessoas e empresas.
  const voltar = useVoltar('/cadastros/cadastros/pessoas').voltar;
  const toast = useToast();
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['cadastros', 'ficha', id], queryFn: () => get<Ficha>(`/cadastros/parceiros/${id}`) });
  const defsExtras = useCamposPersonalizados('cadastros.parceiro');
  // Lote de 19/10 (item 9): o campo escondido em Configurações > Campos das telas some da ficha (o dado continua gravado).
  const tela = useCamposDaTela('cadastros.parceiro');
  const vinculos = useQuery({ queryKey: ['cadastros', 'vinculos', id], queryFn: () => get<GrupoVinculo[]>(`/cadastros/parceiros/${id}/vinculos`) });
  const [editar, setEditar] = useState(false);
  const [novoContato, setNovoContato] = useState(false);
  const [arquivar, setArquivar] = useState(false);
  const [mesclar, setMesclar] = useState(false);
  const [destino, setDestino] = useState<string | null>(null);
  const [sub, setSub] = useState<'enderecos' | 'contas-bancarias' | null>(null);
  // Fase 2 (W19, K5): "copiar do endereço principal" preenche os campos vazios do endereço novo (padrão `ajuste` do Formulario).
  const [copia, setCopia] = useState<{ n: number; dados: Record<string, unknown> }>({ n: 0, dados: {} });
  const recarregar = () => qc.invalidateQueries({ queryKey: ['cadastros'] });

  // Review r6 spec (A2): parceiro arquivado volta às listas (mesclado não: o destino é quem ficou).
  const acDesarquivar = useMutation({
    mutationFn: () => post(`/cadastros/parceiros/${id}/desarquivar`),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
      await recarregar();
      toast.sucesso(t('ficha.desarquivado'));
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const acMesclar = useMutation({
    mutationFn: () => post(`/cadastros/parceiros/${id}/mesclar`, { destino_id: destino }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
      navegar(`/cadastros/cadastros/parceiro/${destino}`, { replace: true });
    },
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });
  const remover = useMutation({
    mutationFn: ({ tipo, subId }: { tipo: string; subId: string }) => del(`/cadastros/parceiros/${id}/${tipo}/${subId}`),
    onSuccess: recarregar,
    onError: (e) => toast.erro(mensagemDeErro(e)),
  });

  if (q.isPending) return <Carregando />;
  if (q.isError) return <Erro erro={q.error} aoTentar={() => q.refetch()} />;
  const p = q.data;
  const podeEditar = s.pode('cadastros.editar') && !p.arquivado;
  // Val5 (29/10): arquivar e desarquivar pedem `cadastros.arquivar`.
  const podeDesarquivar = s.pode('cadastros.arquivar') && p.arquivado && !p.mesclado_em;
  const br = !p.pais || p.pais === 'BR';
  const principal = p.enderecos.find((e) => e.tipo === 'principal') ?? null;
  // Fase 2 (W19, K5): a chave Pix com a máscara e a validação do tipo escolhido; trocar o tipo limpa a chave.
  const pixDoTipo = (tipo: TipoPix | null) => (v: Record<string, unknown>) => (v.chave_pix_tipo ?? null) === tipo;
  const pixInvalida = (tipo: TipoPix) => (v: unknown) => (chavePixValida(tipo, String(v ?? '')) ? null : t('pix.invalida'));

  const camposSub: Record<'enderecos' | 'contas-bancarias', DefCampo[]> = {
    enderecos: [
      { nome: 'tipo', rotulo: t('campos.tipoEndereco'), tipo: 'selecao', obrigatorio: true, largura: 'meia', opcoes: ['principal', 'cobranca', 'entrega', 'outro'].map((v) => ({ valor: v, rotulo: t(`enderecoTipo.${v}`) })) },
      // Fase 2 (W5): no BR, CEP com máscara e consulta pelo servidor (só preenche vazios) e UF entre as 27.
      br
        ? {
            nome: 'cep',
            rotulo: t('campos.cep'),
            tipo: 'cep',
            largura: 'meia',
            preencher: async (cep) => {
              const r = await consultar('cep', cep);
              if (r.situacao !== 'encontrado' || !r.dados) return null;
              const { cep: _cep, ...resto } = enderecoDaConsulta(r.dados);
              void _cep;
              return resto;
            },
          }
        : { nome: 'cep', rotulo: t('campos.codigoPostal'), tipo: 'texto', largura: 'meia', sensivel: 'oculto' },
      { nome: 'logradouro', rotulo: t('campos.logradouro'), tipo: 'texto', obrigatorio: true },
      { nome: 'numero', rotulo: t('campos.numero'), tipo: 'texto', largura: 'terco', sensivel: 'oculto' },
      { nome: 'complemento', rotulo: t('campos.complemento'), tipo: 'texto', largura: 'terco' },
      { nome: 'bairro', rotulo: t('campos.bairro'), tipo: 'texto', largura: 'terco' },
      { nome: 'cidade', rotulo: t('campos.cidade'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
      br ? { nome: 'uf', rotulo: t('campos.uf'), tipo: 'selecao', largura: 'meia', opcoes: opcoesUf() } : { nome: 'uf', rotulo: t('campos.estadoProvincia'), tipo: 'texto', largura: 'meia' },
    ],
    // Fase 2 (W5): tipo da chave Pix (validada pelo tipo no servidor), tipo de conta, código do banco, documento do titular;
    // IBAN e SWIFT só para cadastro de fora do Brasil.
    'contas-bancarias': [
      { nome: 'banco', rotulo: t('campos.banco'), tipo: 'texto', obrigatorio: true, largura: 'meia' },
      { nome: 'banco_codigo', rotulo: t('campos.bancoCodigo'), tipo: 'texto', largura: 'meia' },
      { nome: 'agencia', rotulo: t('campos.agencia'), tipo: 'texto', largura: 'terco' },
      { nome: 'conta', rotulo: t('campos.conta'), tipo: 'texto', largura: 'terco' },
      { nome: 'conta_tipo', rotulo: t('campos.contaTipo'), tipo: 'selecao', largura: 'terco', opcoes: (['corrente', 'poupanca', 'pagamento'] as const).map((x) => ({ valor: x, rotulo: t(`valores.contaTipo.${x}`) })) },
      { nome: 'chave_pix_tipo', rotulo: t('campos.chavePixTipo'), tipo: 'selecao', largura: 'meia', limpa: ['chave_pix'], opcoes: TIPOS_PIX.map((x) => ({ valor: x, rotulo: t(`valores.pixTipo.${x}`) })) },
      { nome: 'chave_pix', rotulo: t('campos.chavePix'), tipo: 'documento', documento: 'cpf', largura: 'meia', visivel: pixDoTipo('cpf'), validar: pixInvalida('cpf') },
      { nome: 'chave_pix', rotulo: t('campos.chavePix'), tipo: 'documento', documento: 'cnpj', largura: 'meia', visivel: pixDoTipo('cnpj'), validar: pixInvalida('cnpj') },
      { nome: 'chave_pix', rotulo: t('campos.chavePix'), tipo: 'email', largura: 'meia', sensivel: 'pix', visivel: pixDoTipo('email') },
      {
        nome: 'chave_pix',
        rotulo: t('campos.chavePix'),
        tipo: 'telefone',
        largura: 'meia',
        sensivel: 'pix',
        placeholder: '(11) 98765-4321',
        transformar: mascaraTelefone,
        validar: pixInvalida('telefone'),
        visivel: pixDoTipo('telefone'),
      },
      {
        nome: 'chave_pix',
        rotulo: t('campos.chavePix'),
        tipo: 'texto',
        largura: 'meia',
        sensivel: 'pix',
        placeholder: '00000000-0000-0000-0000-000000000000',
        transformar: mascaraChaveAleatoria,
        validar: pixInvalida('aleatoria'),
        visivel: pixDoTipo('aleatoria'),
      },
      // Sem tipo: texto livre, como antes (o servidor deduz o tipo pelo valor).
      { nome: 'chave_pix', rotulo: t('campos.chavePix'), tipo: 'texto', largura: 'meia', sensivel: 'pix', visivel: pixDoTipo(null) },
      { nome: 'titular', rotulo: t('campos.titular'), tipo: 'texto', largura: 'meia', sensivel: 'nome' },
      br
        ? { nome: 'titular_documento', rotulo: t('campos.titularDocumento'), tipo: 'documento', documento: 'qualquer', largura: 'meia' }
        : { nome: 'titular_documento', rotulo: t('campos.titularDocumentoExterior'), tipo: 'texto', largura: 'meia', sensivel: 'documento' },
      ...(br
        ? []
        : ([
            { nome: 'iban', rotulo: t('campos.iban'), tipo: 'texto', largura: 'meia', sensivel: 'conta' },
            { nome: 'swift', rotulo: t('campos.swift'), tipo: 'texto', largura: 'meia' },
          ] satisfies DefCampo[])),
    ],
  };

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {podeEditar && (
        <AcaoPrimaria>
          {s.pode('cadastros.mesclar') && (
            <Botao icone={<GitMerge className="size-4" />} onClick={() => setMesclar(true)}>
              {t('acoes.mesclar')}
            </Botao>
          )}
          {s.pode('cadastros.arquivar') && (
            <Botao variante="perigo" icone={<Archive className="size-4" />} onClick={() => setArquivar(true)}>
              {tn('comum.arquivar')}
            </Botao>
          )}
          <Botao variante="primario" icone={<Pencil className="size-4" />} onClick={() => setEditar(true)}>
            {tn('comum.editar')}
          </Botao>
        </AcaoPrimaria>
      )}
      {podeDesarquivar && (
        <AcaoPrimaria>
          <Botao variante="primario" icone={<ArchiveRestore className="size-4" />} carregando={acDesarquivar.isPending} onClick={() => acDesarquivar.mutate()}>
            {t('acoes.desarquivar')}
          </Botao>
        </AcaoPrimaria>
      )}
      <button type="button" onClick={voltar} className="flex w-fit items-center gap-1 text-sm text-tinta-3 hover:text-tinta-1">
        <ArrowLeft className="size-4" aria-hidden />
        {tn('comum.voltar')}
      </button>

      <div className="flex min-w-0 flex-wrap items-center gap-3">
        <span className="flex size-12 shrink-0 items-center justify-center rounded-xl border border-borda bg-sup-2 text-tinta-2">
          {p.tipo === 'empresa' ? <Building2 className="size-6" aria-hidden /> : <User className="size-6" aria-hidden />}
        </span>
        <div className="min-w-0 flex-1">
          {/* r8 visual B5: nome longo quebra em até 2 linhas (com o title do texto inteiro) em vez de cortar numa só. */}
          <TextoCortado como="h2" linhas={2} className="text-xl font-semibold break-words">
            {nomePessoa(p.nome)}
          </TextoCortado>
          <p className="flex flex-wrap items-center gap-2 text-sm text-tinta-3">
            <SelosPapel p={p} />
            <SeloInadimplente m={p.inadimplencia} />
            {!br && <Selo cor="azul">{`${t('ficha.exterior')}: ${nomePais(p.pais)}`}</Selo>}
            {p.arquivado && <Selo cor="perigo">{t('ficha.arquivado')}</Selo>}
            {p.tags.map((tag) => (
              <Selo key={tag.id} cor={tag.cor as CorSelo}>
                {tag.nome}
              </Selo>
            ))}
          </p>
        </div>
      </div>

      <div className="grid min-w-0 gap-4 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-4 lg:col-span-2">
          {/* V4 (20/10): os cartões da coluna principal abrem recolhidos; a Atividade (Chatter, ao lado) fica como está. */}
          <ProvedorCartoesRecolhidos>
            <Cartao titulo={t('ficha.dados')}>
            <dl className="grid gap-x-8 sm:grid-cols-2">
              <Linha rotulo={br ? (p.tipo === 'empresa' ? t('campos.cnpj') : t('campos.cpf')) : p.documento_tipo ? rotuloTipoDocumento(p.documento_tipo) : t('campos.registroExterior')}>
                <span className="numero">{(p.documento_tipo && !br ? formatarDocumentoPais(p.documento_tipo, p.documento) : documentoFormatado(p.documento)) || '-'}</span>
              </Linha>
              {tela.visivel('codigo') && (
                <Linha rotulo={t('campos.codigo')}>
                  <span className="numero">{p.codigo || '-'}</span>
                </Linha>
              )}
              {!br && <Linha rotulo={t('campos.pais')}>{nomePais(p.pais)}</Linha>}
              {!br && p.moeda_padrao && (
                <Linha rotulo={t('campos.moedaPadrao')}>
                  <span className="numero">{p.moeda_padrao}</span>
                </Linha>
              )}
              {tela.visivel('setor_id') && <Linha rotulo={t('campos.setor')}>{p.setor_nome ?? '-'}</Linha>}
              {tela.visivel('email') && (
              <Linha rotulo={t('campos.email')}>
                {p.email ? (
                  // r8 visual B5: e-mail longo corta com reticências (e o title) dentro da largura da coluna.
                  <a className="inline-flex max-w-full min-w-0 items-center gap-1 align-bottom hover:text-acento-forte" href={`mailto:${p.email}`}>
                    <Mail className="size-3.5 shrink-0" aria-hidden />
                    <TextoCortado className="min-w-0">{email(p.email)}</TextoCortado>
                  </a>
                ) : (
                  '-'
                )}
              </Linha>
              )}
              {tela.visivel('telefone') && (
              <Linha rotulo={t('campos.telefone')}>
                {p.telefone ? (
                  <a className="numero inline-flex items-center gap-1 hover:text-acento-forte" href={`tel:${p.telefone}`}>
                    <Phone className="size-3.5" aria-hidden />
                    {telefoneFormatado(p.telefone)}
                  </a>
                ) : (
                  '-'
                )}
              </Linha>
              )}
              {p.parent_id && tela.visivel('parent_id') && (
                <Linha rotulo={t('campos.empresaVinculada')}>
                  <Link className="hover:text-acento-forte" to={`/cadastros/cadastros/parceiro/${p.parent_id}`}>
                    {nomePessoa(p.parent_nome)}
                  </Link>
                </Linha>
              )}
              {p.cargo && tela.visivel('cargo') && <Linha rotulo={t('campos.cargo')}>{textoLivre(p.cargo, p.nome)}</Linha>}
              {p.inadimplencia && (
                <Linha rotulo={tn('inadimplencia.rotulo')}>
                  <Link data-inadimplencia="" className="text-perigo hover:underline" to={linkTitulosDoParceiro(p.id)} title={tn('inadimplencia.verTitulos')}>
                    {textoInadimplencia(p.inadimplencia)}
                  </Link>
                </Linha>
              )}
              {tela.visivel('ranking_cliente') && (
                <Linha rotulo={t('campos.rankingCliente')}>
                  <Estrelas n={p.ranking_cliente} />
                </Linha>
              )}
              <Linha rotulo={t('campos.origem')}>{rotuloValor('origem', p.origem)}</Linha>
              {/* Review r12 visual M1: rótulo da definição e valor pelo formatador único (data no idioma, texto censurado). */}
              {linhasExtras(defsExtras, p.extras, p.nome, ...p.contatos.map((c) => c.nome)).map((l) => (
                <Linha key={l.chave} rotulo={l.rotulo}>
                  {l.valor}
                </Linha>
              ))}
            </dl>
            {p.observacoes && tela.visivel('observacoes') && <p className="mt-3 border-t border-borda pt-3 text-sm whitespace-pre-wrap text-tinta-2">{textoLivre(p.observacoes, p.nome, ...p.contatos.map((c) => c.nome))}</p>}
          </Cartao>
          {/* V1 verticais (20/10): em quais verticais (empresas) o cadastro está, com o histórico. */}
          <CartaoVerticais entidade="cadastros.parceiro" registroId={p.id} podeEditar={podeEditar} />

          <CartaoFiscal p={p} podeEditar={podeEditar} recarregar={recarregar} />
          <CartaoVinculos p={p} podeEditar={podeEditar} recarregar={recarregar} />
          {/* Fase 2 (W25, K14): condições de compra do fornecedor (padrão no Compras), só com o Compras instalado. */}
          {p.e_fornecedor && s.instalado('compras') && <CartaoFornecimento p={p} podeEditar={podeEditar} />}
          {/* Portal do Cliente (Onda 1): cartões dos módulos na ficha do cliente (a aba Portal: acessos, documentos e trilha). */}
          {p.e_cliente &&
            !p.parent_id &&
            modulosWeb
              .filter((m) => m.cartaoNaFichaCliente && s.instalado(m.slug))
              .map((m) => {
                const C = m.cartaoNaFichaCliente!;
                return <C key={m.slug} parceiroId={p.id} nome={p.nome} contatos={p.contatos.filter((c) => !c.adicional).map((c) => ({ id: c.id, nome: c.nome, email: c.email }))} />;
              })}

          {p.tipo === 'empresa' && (
            <Cartao
              titulo={t('ficha.contatos')}
              acao={
                podeEditar &&
                s.pode('cadastros.criar') && (
                  <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => setNovoContato(true)}>
                    {t('acoes.adicionarContato')}
                  </Botao>
                )
              }
            >
              {p.contatos.length === 0 ? (
                <p className="text-sm text-tinta-3">{t('ficha.semItens')}</p>
              ) : (
                <ul className="divide-y divide-borda">
                  {p.contatos.map((c) => (
                    <li key={c.id}>
                      <Link to={`/cadastros/cadastros/parceiro/${c.id}`} className="flex min-w-0 flex-wrap justify-between gap-x-4 py-2 text-sm hover:text-acento-forte">
                        <span className="font-medium">
                          {nomePessoa(c.nome)}
                          {/* rl0211-1 SPEC-B2: o contato que tem a empresa como adicional leva a marca, como na ficha do CRM. */}
                          {c.adicional && <span className="ml-1 text-xs font-normal text-tinta-3" data-contato-adicional="">({t('crm.aj.adicional')})</span>}
                        </span>
                        <TextoCortado className="text-tinta-3">{[textoLivre(c.cargo, c.nome), email(c.email)].filter(Boolean).join(' / ')}</TextoCortado>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </Cartao>
          )}

          {(['enderecos', 'contas-bancarias'] as const).filter((tipo) => tipo !== 'enderecos' || tela.visivel('endereco')).map((tipo) => {
            const itens = tipo === 'enderecos' ? p.enderecos : p.contas_bancarias;
            return (
              <Cartao
                key={tipo}
                titulo={t(tipo === 'enderecos' ? 'ficha.enderecos' : 'ficha.contas')}
                acao={
                  podeEditar && (
                    <Botao variante="fantasma" tamanho="sm" icone={<Plus className="size-4" />} onClick={() => (setCopia({ n: 0, dados: {} }), setSub(tipo))}>
                      {t('acoes.adicionar')}
                    </Botao>
                  )
                }
              >
                {itens.length === 0 ? (
                  <p className="text-sm text-tinta-3">{t('ficha.semItens')}</p>
                ) : (
                  <ul className="divide-y divide-borda">
                    {itens.map((i) => (
                      <li key={i.id} className="flex min-w-0 items-start justify-between gap-3 py-2 text-sm">
                        <span className="min-w-0 break-words">
                          {'logradouro' in i ? (
                            <>
                              <Selo>{t(`enderecoTipo.${i.tipo}`)}</Selo> {endereco(i)}
                            </>
                          ) : (
                            <LinhaConta c={i} />
                          )}
                        </span>
                        {podeEditar && (
                          <button type="button" aria-label={tn('comum.excluir')} onClick={() => remover.mutate({ tipo, subId: i.id })} className="shrink-0 rounded p-1 text-tinta-3 hover:text-perigo">
                            <Trash2 className="size-4" />
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </Cartao>
            );
          })}

          {(p.projetos.length > 0 || (vinculos.data?.length ?? 0) > 0) && (
            <Cartao titulo={t('ficha.vinculos')}>
              <div className="flex flex-col gap-4">
                {p.projetos.length > 0 && (
                  <div>
                    <p className="kicker mb-1">{t('ficha.projetos')}</p>
                    <ul>
                      {p.projetos.map((pr) => (
                        <li key={pr.id} className="flex justify-between gap-3 py-1 text-sm">
                          <Link to="/cadastros/cadastros/projetos" className="hover:text-acento-forte">
                            {textoLivre(pr.nome, p.nome, ...p.contatos.map((c) => c.nome))}
                          </Link>
                          <Selo>{t(`status.${pr.status}`)}</Selo>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {vinculos.data?.map((g) => (
                  <div key={g.modulo}>
                    <p className="kicker mb-1">{t(`vinculos.${g.modulo}`, { defaultValue: humanizar(g.modulo) })}</p>
                    <ul>
                      {g.itens.map((i) => {
                        // r6 visual M4 e B3: texto no idioma e na moeda do app (nucleo/vinculos.ts) e title no que pode cortar.
                        const v = textoVinculo(g.modulo, i);
                        // Opção de nomes (r9 visual M2): título livre (oportunidade, fatura) que cita o parceiro ou um contato.
                        const rotulo = textoLivre(v.rotulo, p.nome, ...p.contatos.map((c) => c.nome));
                        return (
                          <li key={i.id} className="flex justify-between gap-3 py-1 text-sm">
                            <Link to={i.link} title={rotulo} className="truncate hover:text-acento-forte">
                              {rotulo}
                            </Link>
                            {v.detalhe && <span className="shrink-0 text-tinta-3">{textoLivre(v.detalhe, p.nome, ...p.contatos.map((c) => c.nome))}</span>}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            </Cartao>
          )}
          </ProvedorCartoesRecolhidos>
        </div>

        <Cartao className="h-fit">
          <Chatter
            entidade="cadastros.parceiro"
            registroId={p.id}
            titulo={p.nome}
            nomes={[p.nome, ...p.contatos.map((c) => c.nome)]}
            rotulosCampos={{
              nome: t('campos.nome'),
              email: t('campos.email'),
              telefone: t('campos.telefone'),
              ranking_cliente: t('campos.rankingCliente'),
              documento: t('campos.documento'),
              pais: t('campos.pais'),
              moeda_padrao: t('campos.moedaPadrao'),
              e_parceiro: t('campos.parceiroComercial'),
              razao_social: t('campos.razaoSocial'),
              nome_fantasia: t('campos.nomeFantasia'),
              data_abertura: t('campos.dataAbertura'),
              porte: t('campos.porte'),
              natureza_juridica: t('campos.naturezaJuridica'),
              situacao_cadastral: t('campos.situacaoCadastral'),
              situacao_data: t('campos.situacaoData'),
              capital_social_centavos: t('campos.capitalSocial'),
              isento_ie: t('campos.isentoIe'),
              inscricao_estadual: t('campos.inscricaoEstadual'),
              inscricao_municipal: t('campos.inscricaoMunicipal'),
              produtor_rural: t('campos.produtorRural'),
              inscricoes_produtor: t('campos.inscricoesProdutor'),
              regime_tributario: t('campos.regime'),
              vinculo: t('campos.vinculoTipo'),
            }}
          />
        </Cartao>
      </div>

      <FormParceiro aberto={editar} aoFechar={() => setEditar(false)} parceiro={p} aoSalvar={() => recarregar()} />
      {novoContato && <FormParceiro aberto aoFechar={() => setNovoContato(false)} papelNovo="contato" contatoDe={p.id} aoSalvar={() => recarregar()} />}
      <ArquivarParceiro
        id={p.id}
        aberto={arquivar}
        aoFechar={() => setArquivar(false)}
        aoArquivado={async () => {
          setArquivar(false);
          await qc.invalidateQueries({ queryKey: ['/cadastros/parceiros'] });
          navegar(-1);
        }}
      />
      <Modal
        aberto={mesclar}
        aoFechar={() => setMesclar(false)}
        titulo={t('mesclar.titulo')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setMesclar(false)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" disabled={!destino} carregando={acMesclar.isPending} onClick={() => acMesclar.mutate()}>
              {tn('comum.confirmar')}
            </Botao>
          </>
        }
      >
        <p className="mb-4 text-sm text-tinta-2">{t('mesclar.texto')}</p>
        <Rotulo id="destino" rotulo={t('campos.destino')}>
          <BuscaRemota id="destino" def={{ caminho: '/cadastros/parceiros', params: { tipo: p.tipo }, rotulo: (i) => String(i.nome) }} valor={destino} aoMudar={(v) => setDestino(v === id ? null : v)} />
        </Rotulo>
      </Modal>
      <Modal
        aberto={!!sub}
        aoFechar={() => setSub(null)}
        titulo={sub === 'enderecos' ? t('ficha.enderecos') : t('ficha.contas')}
        rodape={
          <>
            <Botao variante="fantasma" onClick={() => setSub(null)}>
              {tn('comum.cancelar')}
            </Botao>
            <Botao variante="primario" type="submit" form="form-sub">
              {tn('comum.salvar')}
            </Botao>
          </>
        }
      >
        {sub === 'enderecos' && principal && (
          <div className="mb-4">
            <Botao
              tamanho="sm"
              icone={<Copy className="size-4" />}
              onClick={() => {
                const { id: _id, tipo: _tipo, pais: _pais, ...campos } = principal;
                void _id;
                void _tipo;
                void _pais;
                setCopia((c) => ({ n: c.n + 1, dados: campos }));
              }}
            >
              {t('acoes.copiarPrincipal')}
            </Botao>
          </div>
        )}
        {sub && (
          <Formulario
            id="form-sub"
            campos={camposSub[sub]}
            ajuste={sub === 'enderecos' ? copia : undefined}
            inicial={sub === 'enderecos' ? { tipo: principal ? 'cobranca' : 'principal' } : {}}
            aoEnviar={async (v) => {
              await post(`/cadastros/parceiros/${id}/${sub}`, Object.fromEntries(Object.entries(v).map(([k, x]) => [k, x === '' ? null : x])));
              setSub(null);
              await recarregar();
            }}
          />
        )}
      </Modal>
    </div>
  );
}
